import {
  Align,
  Button,
  Distribute,
  FormField,
  Select,
  Spacer,
  SvgTrash,
  Text,
  TextButton,
  TextInput,
  hslaColors,
} from '@loomhq/lens';
import _debounce from 'lodash/debounce';
import Mousetrap from 'mousetrap';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { showConfirmationModal } from 'actions/confirmationModal';
import { endCTAFlow } from 'actions/editor';
import {
  updateVideoCtaEnabled,
  updateVideoCtaModalShow,
  updateVideoCtaMods,
  updateVideoCtaText,
  updateVideoCtaUrl,
} from 'actions/shareVideo';
import {
  requestVideoPlayerCurrentTime,
  requestVideoPlayerPause,
  requestVideoPlayerPlay,
  updateVideoPlayerPlaying,
} from 'actions/videoPlayer';
import { ADD_CUSTOM_VIDEO_CTA_ENABLED } from 'constants/events';
import { ESCAPE, KEY_ESCAPE } from 'constants/keyCodes';
import { KEY_UP } from 'constants/keyEvents';
import { clearCTAWidgetState, saveCTA } from 'creators/editor';
import { updateVideoCtaCreator } from 'creators/shareVideo';
import * as analytics from 'utilities/analytics';

import rainbowSrc from 'img/rainbow-oval.png';

import SvgRoundedCorner from './icons/rounded-corner';
import SvgSlightlyRoundedCorner from './icons/slightly-rounded-corner';
import SvgSquareCorner from './icons/square-corner';
import VideoSectionCard from '../video-section-card';
import getHexFromHsl from '../../../utilities/getHexFromHsl';
import { isHexColor, urlRegex } from '../../../../../shared/validate';

import ColorPicker from 'components/ui-components/color-picker';

import './styles.less';

const CORNER_STYLES = {
  Rounded: { value: 25, icon: <SvgRoundedCorner /> },
  Squared: { value: 0, icon: <SvgSquareCorner /> },
  'Slightly rounded': { value: 4, icon: <SvgSlightlyRoundedCorner /> },
};

const COLOR_ERROR = 'Yikes! Doesn’t look like a valid color';

const PRESET_COLORS = [
  // only lens colors below!
  'orange',
  'teal',
  'yellowDark',
  'blue',
  'offWhite',
  'grey8',
  'white',
].map(colorName => {
  const { h, s, l } = hslaColors[colorName];

  return getHexFromHsl(h, s, l);
});

class CTAForm extends Component {
  state = {
    corner_dropdown: false,
    bg_color_picker: false,
    text_color_picker: false,
    text_color_err: false,
    background_color_err: false,
    url_err: false,
    return_to_time: null,
  };

  render() {
    const {
      bg_color_picker,
      text_color_picker,
      background_color_err,
      text_color_err,
      url_err,
    } = this.state;

    const { ctaEnabled, ctaText, ctaUrl, ctaMods } = this.props;

    const saveEligible = this.saveEligible();

    return (
      <VideoSectionCard>
        <Distribute gap="auto">
          <Text variant="title">Create a call-to-action</Text>
          <span className="navigation-close" onClick={this.clearSettings} />
        </Distribute>
        <Spacer top="medium">
          <FormField labelFor="button-text" label="Button text">
            <TextInput
              autoFocus={true}
              onChange={e => this.handleCtaTextInput(e.target.value)}
              onKeyUp={e => this.handleOnKeyUp(e)}
              id="button-text"
              value={ctaText || ''}
            />
          </FormField>
        </Spacer>
        <Spacer top="medium">
          <FormField
            labelFor="button-link"
            label="Button link (URL)"
            errorMessage={url_err ? 'Yikes! Doesn’t look like a valid URL' : ''}
          >
            <TextInput
              onBlur={() => this.instantValidate()}
              onChange={e => this.handleCtaUrlInput(e.target.value)}
              onKeyUp={e => this.handleOnKeyUp(e)}
              id="button-link"
              value={ctaUrl || ''}
              hasError={url_err}
              type="url"
            />
          </FormField>
        </Spacer>
        <Spacer top="medium">
          <div
            className="picker-input"
            onClick={() =>
              this.setState({
                bg_color_picker: true,
              })
            }
          >
            <FormField
              label="Button color"
              labelFor="button-color"
              errorMessage={background_color_err ? COLOR_ERROR : null}
            >
              <TextInput
                onChange={e =>
                  this.handleCtaBackgroundColorInput(e.target.value)
                }
                onKeyUp={e => this.handleOnKeyUp(e)}
                id="button-color"
                value={(ctaMods || {}).background_color}
                hasError={background_color_err}
              />
            </FormField>
            <img src={rainbowSrc} className="picker-icon" />
            <div className="form-color-picker">
              <ColorPicker
                onClickOutside={() => this.setState({ bg_color_picker: false })}
                show={bg_color_picker}
                disableAlpha={true}
                presetColors={PRESET_COLORS}
                color={(ctaMods || {}).background_color}
                onChangeComplete={color =>
                  this.handleCtaBackgroundColorInput(color.hex)
                }
              />
            </div>
          </div>
        </Spacer>
        <Spacer top="medium">
          <div
            className="picker-input"
            onClick={() =>
              this.setState({
                text_color_picker: true,
              })
            }
          >
            <FormField
              label="Text color"
              labelFor="text-color"
              errorMessage={text_color_err ? COLOR_ERROR : null}
            >
              <TextInput
                onChange={e => this.handleCtaTextColorInput(e.target.value)}
                onKeyUp={e => this.handleOnKeyUp(e)}
                id="text-color"
                value={(ctaMods || {}).color}
                hasError={text_color_err}
              />
            </FormField>
            <img src={rainbowSrc} className="picker-icon" />
            <div className="form-color-picker">
              <ColorPicker
                onClickOutside={() =>
                  this.setState({ text_color_picker: false })
                }
                show={text_color_picker}
                disableAlpha={true}
                presetColors={PRESET_COLORS}
                color={(ctaMods || {}).color}
                onChangeComplete={color =>
                  this.handleCtaTextColorInput(color.hex)
                }
              />
            </div>
          </div>
        </Spacer>
        <Spacer top="medium">
          <FormField label="Button corner style">
            <Select
              selectedOptionValue={this.getBorderRadiusLabel(
                (ctaMods || {}).border_radius
              )}
              onChange={selection => this.handleCornerClick(selection.value)}
              options={Object.entries(CORNER_STYLES).map(([key, value]) => {
                return {
                  value: key,
                  title: key,
                  icon: value.icon,
                };
              })}
            />
          </FormField>
        </Spacer>

        {ctaEnabled && (
          <Align alignment="center">
            <Spacer top="medium" bottom="small">
              <TextButton icon={<SvgTrash />} onClick={this.handleDelete}>
                Delete call-to-action
              </TextButton>
            </Spacer>
          </Align>
        )}

        <div className="flex width:full mt:large">
          <Button
            variant="primary"
            isDisabled={!saveEligible}
            onClick={e => this.save(e)}
            hasFullWidth
          >
            Save call-to-action
          </Button>
        </div>
      </VideoSectionCard>
    );
  }

  componentDidMount() {
    if (this.props.videoDuration) {
      this.movePlayerToEnd();
    }

    Mousetrap.bind(KEY_ESCAPE, this.props.clearCTAWidgetState, KEY_UP);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.ctaMods && this.props.ctaMods) {
      this.setState({ button_corner_style: this.props.ctaMods.border_radius });
    }

    if (!prevProps.videoDuration && this.props.videoDuration) {
      this.movePlayerToEnd();
    }
  }

  // move back to the time we moved ahead from
  // to ensure that it is in a good state after being brought back
  // to the previous time, we force the player to play and then
  // pause after a tick so all the right video element events
  // are emitted
  componentWillUnmount() {
    Mousetrap.unbind(KEY_ESCAPE, KEY_UP);

    if (this.state.return_to_time) {
      this.props.requestVideoPlayerCurrentTime(this.state.return_to_time);
      this.props.updateVideoPlayerPlaying();
      this.props.requestVideoPlayerPlay();
      setTimeout(() => this.props.requestVideoPlayerPause());

      this.setState({ return_to_time: null });
    }
  }

  // we want to show the actual last frame in the background during preview
  movePlayerToEnd = () => {
    this.setState({ return_to_time: this.props.currentTime });

    this.props.requestVideoPlayerCurrentTime(this.props.videoDuration);
  };

  handleCornerClick = corner => {
    const getCornerFromTitle = title => {
      return CORNER_STYLES[title].value;
    };

    this.setState({ corner_dropdown: false });

    this.handleCtaRadiusInput(getCornerFromTitle(corner));
    this.validate();
  };

  handleOnKeyUp = e => {
    if (e.keyCode === ESCAPE) {
      this.props.clearCTAWidgetState();
    }
  };

  getBorderRadiusLabel = radius => {
    if (radius === CORNER_STYLES['Rounded'].value) {
      return 'Rounded';
    }

    if (radius === CORNER_STYLES['Squared'].value) {
      return 'Squared';
    }

    return 'Slightly rounded';
  };

  handleCtaBackgroundColorInput = val => {
    const { ctaMods } = this.props;

    this.setState({ background_color_err: false });

    // length longer than hex code length
    if (val.length > 7) {
      return;
    }

    this.props.updateVideoCtaMods({ ...ctaMods, background_color: val });
    this.validate();
  };

  handleCtaTextColorInput = val => {
    const { ctaMods } = this.props;

    this.setState({ text_color_err: false });

    // length longer than hex code length
    if (val.length > 7) {
      return;
    }

    this.props.updateVideoCtaMods({ ...ctaMods, color: val });
    this.validate();
  };

  handleCtaRadiusInput = val => {
    let newVal;

    this.setState({ border_radius_err: false });

    if (!Number.isInteger(Number(val))) {
      return;
    }

    if (val > 200) {
      newVal = 199;
    } else if (val < 0) {
      newVal = 0;
    } else {
      newVal = val;
    }

    const { ctaMods } = this.props;

    this.props.updateVideoCtaMods({ ...ctaMods, border_radius: newVal });
    this.validate();
  };

  handleCtaUrlInput = val => {
    this.setState({ url_err: false });
    this.props.updateVideoCtaUrl(val);
  };

  handleCtaTextInput = val => {
    this.props.updateVideoCtaText(val);
    this.validate();
  };

  saveEligible = () => {
    const { background_color_err, text_color_err, url_err } = this.state;
    const { ctaMods, ctaText, ctaUrl } = this.props;

    // we're eligible to save if there are no errors and the required fields are present
    return (
      !(background_color_err || text_color_err || url_err) &&
      ctaMods &&
      ctaText &&
      ctaUrl
    );
  };

  save = async () => {
    this.instantValidate();

    if (!this.saveEligible()) {
      return;
    }

    const { ctaMods } = this.props;

    await this.props.saveCTA();

    this.props.showConfirmationModal('Call-to-action saved');
    analytics.track(ADD_CUSTOM_VIDEO_CTA_ENABLED, ctaMods || {});
  };

  clearSettings = () => {
    this.props.clearCTAWidgetState();
  };

  handleDelete = async () => {
    this.props.clearCTAWidgetState();

    await this.props.updateVideoCtaCreator();

    this.props.showConfirmationModal('Call-to-action deleted');
  };

  instantValidate = () => {
    const { ctaMods, ctaUrl } = this.props;

    if (ctaMods && ctaMods.color && !isHexColor(ctaMods.color)) {
      this.setState({ text_color_err: true });
    }

    if (
      ctaMods &&
      ctaMods.background_color &&
      !isHexColor(ctaMods.background_color)
    ) {
      this.setState({ background_color_err: true });
    }

    if (
      ctaMods &&
      ctaMods.border_radius &&
      !Number.isInteger(Number(ctaMods.border_radius))
    ) {
      this.setState({ border_radius_err: true });
    }

    if (ctaUrl && !urlRegex({ exact: true, strict: false }).test(ctaUrl)) {
      this.setState({ url_err: true });
    }
  };

  validate = _debounce(this.instantValidate, 300);
}

const mapStateToProps = state => {
  const {
    cta_enabled: ctaEnabled,
    cta_mods: ctaMods,
    cta_text: ctaText,
    cta_url: ctaUrl,
    cta_modal_show: ctaModalShow,
    current_user_is_owner: currentUserIsOwner,
    video,
  } = state.share_video;

  const { duration: videoDuration } = (video && video.video_properties) || {};
  const currentTime = state.video_player.current_time;

  return {
    ctaEnabled,
    ctaMods,
    ctaText,
    ctaUrl,
    ctaModalShow,
    currentUserIsOwner,
    videoDuration,
    currentTime,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateVideoCtaEnabled: enabled => dispatch(updateVideoCtaEnabled(enabled)),
    updateVideoCtaUrl: url => dispatch(updateVideoCtaUrl(url)),
    updateVideoCtaText: text => dispatch(updateVideoCtaText(text)),
    updateVideoCtaMods: mods => dispatch(updateVideoCtaMods(mods)),
    updateVideoCtaCreator: () => dispatch(updateVideoCtaCreator()),
    updateVideoCtaModalShow: show => dispatch(updateVideoCtaModalShow(show)),
    endCTAFlow: () => dispatch(endCTAFlow()),
    requestVideoPlayerCurrentTime: time =>
      dispatch(requestVideoPlayerCurrentTime(time)),
    requestVideoPlayerPlay: () => dispatch(requestVideoPlayerPlay()),
    requestVideoPlayerPause: () => dispatch(requestVideoPlayerPause()),
    updateVideoPlayerPlaying: () => dispatch(updateVideoPlayerPlaying()),
    clearCTAWidgetState: () => dispatch(clearCTAWidgetState()),
    saveCTA: () => dispatch(saveCTA()),
    showConfirmationModal: text => dispatch(showConfirmationModal(text)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CTAForm);
